import type { EN_DECK } from './en';

/**
 * हिन्दी — the DEC-053 proving run.
 *
 * Register: plain, warm, respectful आप-form — the customer is an anxious
 * first-timer on a phone, not a form-filler. No bureaucratic Sanskritised
 * stiffness where an everyday word exists; established Indian-English terms
 * (PAN, Aadhaar, DigiLocker, OTP, video-KYC, selfie) stay in Latin script, as
 * Indian users actually read them. `**bold**` markers and `{slots}` are
 * preserved exactly.
 *
 * REVIEW STATUS: the CONSENT-ADJACENT set is VCIP-REG-REVIEWED (DEC-053 rider,
 * 2026-08-06): one legal-substance rejection (declined.body — the original
 * phrasing implied consent withdrawal is only possible AFTER the retention
 * period, restricting a DPDP statutory right) and three polish edits are
 * APPLIED below; everything else in the set was approved, including the
 * देता/देती slash-form for the legal affirmation and the सहमति/अनुमति
 * consent-vs-permission split, which must not be disturbed. VCIP-REG flagged
 * the EN SOURCE ambiguity upstream (flow.ts declined.body — "after the
 * mandatory retention period" must attach to erasure only) for notice-owner
 * ratification before other locales are authored. The whole deck still rides
 * the standing human-SME linguistic review before production cutover
 * (DO-NOT-COPY #6) — that gate is separate and undischarged. Preview only.
 */
export const HI_DECK: Record<keyof typeof EN_DECK, string> = {
  // ── documentSteps deck ─────────────────────────────────────────────────────
  'droom.capture.grade.live': 'आपके डिवाइस पर गुणवत्ता-जाँची गई',
  'droom.capture.grade.upload': 'अपलोड की गई — कम साक्ष्य श्रेणी',
  'droom.capture.grade.upload.chip': 'कम श्रेणी',
  'droom.capture.grade.upload.explain':
    'अपलोड की गई कॉपी की साक्ष्य श्रेणी कम होती है — आपके सत्यापन अधिकारी कॉल पर आपके साथ आपके दस्तावेज़ों की पुष्टि करेंगे।',
  'droom.selfie.title': 'एक साफ़ selfie लें',
  'droom.selfie.sub': 'समान रोशनी में कैमरे की ओर देखें और अपना पूरा चेहरा अंडाकार घेरे के अंदर रखें।',
  'droom.selfie.hint.lineup': 'अपना पूरा चेहरा अंडाकार घेरे के अंदर रखें।',
  'droom.selfie.hint.toodark': 'रोशनी थोड़ी कम लग रही है — किसी उजली जगह जाएँ।',
  'droom.selfie.hint.toobright': 'रोशनी बहुत तेज़ लग रही है — सीधी रोशनी से हटकर खड़े हों।',
  'droom.selfie.primer': 'आपकी selfie के लिए हम आपका front कैमरा इस्तेमाल करेंगे।',
  'droom.selfie.done.title': 'Selfie ले ली गई',
  'droom.selfie.done.sub': 'आपकी selfie सुरक्षित रूप से सहेज ली गई है — यहाँ और कुछ नहीं करना है।',
  'droom.selfiedoc.title': 'अपना दस्तावेज़ चेहरे के पास रखें',
  'droom.selfiedoc.sub':
    'एक ही फ़ोटो में आप और आपका दस्तावेज़ साथ दिखें — चेहरा अंडाकार घेरे में और दस्तावेज़ बॉक्स में रखें।',
  'droom.selfiedoc.hint.lineup': 'चेहरा अंडाकार घेरे में और दस्तावेज़ बॉक्स में रखें।',
  'droom.selfiedoc.hint.readable': 'दस्तावेज़ को सीधा रखें ताकि हर विवरण पढ़ा जा सके।',
  'droom.selfiedoc.primer':
    'हम आपका front कैमरा इस्तेमाल करेंगे — अपना PAN कार्ड, driving licence या passport हाथ में तैयार रखें। कृपया अपना Aadhaar कार्ड कैमरे के सामने न रखें — यहाँ उसकी कभी ज़रूरत नहीं है।',
  'droom.selfiedoc.done.title': 'फ़ोटो ले ली गई',
  'droom.selfiedoc.done.sub': 'दस्तावेज़ के साथ आपकी फ़ोटो सुरक्षित रूप से सहेज ली गई है।',
  'droom.selfiedoc.unavailable':
    'यह चरण अभी इस सिस्टम पर उपलब्ध नहीं है — आपके सत्यापन अधिकारी कॉल पर आपके साथ यह फ़ोटो लेंगे।',
  'droom.capture.assessing': 'आपके डिवाइस पर गुणवत्ता जाँची जा रही है…',
  'droom.capture.uploading': 'सुरक्षित रूप से अपलोड हो रहा है…',
  'droom.capture.preview.ok': 'साफ़ दिख रहा है — यह फ़ोटो इस्तेमाल करें, या दोबारा लें।',
  'droom.capture.retake.note': 'चलिए एक बार और लेते हैं — दोबारा लेना बिल्कुल सामान्य है।',
  'droom.capture.camera.starting': 'आपका कैमरा शुरू हो रहा है…',
  'droom.capture.camera.checking': 'कैमरे की अनुमति जाँची जा रही है…',
  'droom.capture.camera.notready': 'कैमरा अभी शुरू हो रहा है — एक पल रुककर फिर कोशिश करें।',
  'droom.capture.fallback.oncall': 'आगे बढ़ें — यह फ़ोटो कॉल पर अपने सत्यापन अधिकारी के साथ लें',
  'droom.capture.uploadfailed': 'अपलोड नहीं हो सका — अपना इंटरनेट जाँचकर फिर कोशिश करें।',
  'droom.upload.title': 'अपने दस्तावेज़ जोड़ें',
  'droom.upload.sub': 'नीचे दिए हर दस्तावेज़ की एक साफ़ फ़ोटो अपलोड करें।',
  'droom.upload.choose': 'फ़ोटो चुनें',
  'droom.upload.replaceNot':
    'हमें मिली पहली कॉपी ही मान्य होती है — उसे यहाँ बदला नहीं जा सकता।',
  'droom.upload.liveonly': 'यह दस्तावेज़ कॉल पर आपके सत्यापन अधिकारी के साथ लाइव खींचा जाता है।',
  'droom.upload.required': 'आवश्यक',
  'droom.upload.optional': 'वैकल्पिक',
  'droom.upload.progress': 'सुरक्षित रूप से अपलोड हो रहा है…',
  'droom.upload.notavailable.title': 'दस्तावेज़ अपलोड यहाँ अभी उपलब्ध नहीं है',
  'droom.upload.notavailable.body':
    'यह सिस्टम अभी दस्तावेज़ अपलोड नहीं लेता — आपके दस्तावेज़ कॉल पर आपके सत्यापन अधिकारी के साथ जाँचे जाएँगे।',
  'droom.upload.loading': 'देखा जा रहा है कि हमें कौन-से दस्तावेज़ चाहिए…',
  'droom.upload.loaderror': 'आपके दस्तावेज़ों की सूची नहीं मिल पाई। कृपया फिर कोशिश करें।',
  'droom.capture.reject.blur': 'फ़ोटो धुँधली आई — फ़ोन स्थिर रखकर फिर कोशिश करें।',
  'droom.capture.reject.dark': 'रोशनी थोड़ी कम है — बेहतर रोशनी में जाकर फिर कोशिश करें।',
  'droom.capture.reject.bright': 'तेज़ चमक आ रही है — रोशनी से थोड़ा घुमाकर दोबारा लें।',
  'droom.capture.reject.small':
    'फ़ोटो साफ़ पढ़ने के लिए बहुत छोटी है — पास आएँ ताकि दस्तावेज़ फ़्रेम में भर जाए, फिर दोबारा लें।',
  'droom.capture.reject.unreadable': 'यह फ़ोटो प्रोसेस नहीं हो पाई — कृपया फिर कोशिश करें।',
  'droom.upload.reject.file': 'यह फ़ाइल काम नहीं करेगी — कृपया एक साफ़ फ़ोटो (JPG या PNG) अपलोड करें।',
  'droom.upload.reject.toolarge': 'यह फ़ाइल बहुत बड़ी है — कृपया 8 MB से छोटी फ़ोटो अपलोड करें।',
  'droom.upload.reject.blur': 'यह फ़ोटो धुँधली लग रही है — साफ़ फ़ोटो से सत्यापन जल्दी होता है।',
  'droom.upload.reject.dark':
    'यह फ़ोटो अँधेरी लग रही है — बेहतर रोशनी में ली गई फ़ोटो से सत्यापन जल्दी होता है।',
  'droom.upload.reject.bright':
    'यह फ़ोटो चमक से धुली लग रही है — बिना चमक वाली फ़ोटो से सत्यापन जल्दी होता है।',
  'droom.upload.reject.small':
    'यह फ़ोटो पढ़ने के लिए बहुत छोटी है — बड़ी और साफ़ फ़ोटो से सत्यापन जल्दी होता है।',
  'droom.upload.reject.unreadable':
    'हम यह फ़ाइल पढ़ नहीं पाए — कृपया एक साफ़ फ़ोटो (JPG या PNG) चुनें।',

  // ── support deck ───────────────────────────────────────────────────────────
  'droom.support.trigger': 'कोई दिक्कत हो रही है?',
  'droom.support.title': 'इस चरण में मदद पाएँ',
  'droom.support.close': 'बंद करें',
  'droom.support.intro.named': 'यहाँ कुछ काम नहीं कर रहा हो, तो {tenantName} आपकी मदद कर सकता है।',
  'droom.support.intro.generic': 'यहाँ कुछ काम नहीं कर रहा हो, तो मदद उपलब्ध है।',
  'droom.support.none':
    'इस संस्था ने यहाँ कोई सहायता संपर्क नहीं दिया है। जिस संदेश से आपको यह लिंक मिला, उसी का जवाब दें — एक व्यक्ति आपकी मदद करेगा।',
  'droom.support.boundary':
    'सहायता टीम इस पेज के इस्तेमाल में आपकी मदद कर सकती है। आपके सत्यापन का निर्णय केवल आपके सत्यापन अधिकारी, कॉल पर करते हैं।',
  'droom.support.phone.label': 'सहायता को कॉल करें',
  'droom.support.email.label': 'सहायता को ईमेल करें',
  'droom.support.web.label': 'सहायता पेज खोलें',
  'droom.support.hours': 'उपलब्ध: {hours}',
  'droom.support.oncall':
    'आप अभी अपने सत्यापन अधिकारी के साथ कॉल पर हैं — उनसे सीधे पूछें, वे आपकी मदद करेंगे।',
  'droom.support.hint.welcome': 'देख लें कि आपने हमारा भेजा सबसे नया लिंक ही खोला है।',
  'droom.support.hint.schedule': 'जो भी समय आपको ठीक लगे चुन लें — बाद में बदला जा सकता है।',
  'droom.support.hint.consent':
    'आगे बढ़ने के लिए आपकी सहमति ज़रूरी है। पूरा पढ़ लें — आपकी सहमति से पहले कुछ भी साझा नहीं होता।',
  'droom.support.hint.confirm':
    'हर विवरण ध्यान से जाँचें। कुछ गलत हो तो कॉल पर अपने सत्यापन अधिकारी को बताएँ।',
  'droom.support.hint.pan': 'अपना PAN ठीक वैसे ही लिखें जैसा कार्ड पर छपा है, बिना स्पेस के।',
  'droom.support.hint.aadhaar': 'अपने Aadhaar से जुड़े मोबाइल नंबर पर आए OTP का इस्तेमाल करें।',
  'droom.support.hint.capture':
    'समान रोशनी वाली जगह चुनें, फ़ोन स्थिर रखें, और पूरा दस्तावेज़ फ़्रेम के अंदर रखें।',
  'droom.support.hint.selfie':
    'समान रोशनी में कैमरे की ओर देखें और पूरा चेहरा अंडाकार घेरे में रखें।',
  'droom.support.hint.waiting':
    'यह पेज खुला रखें — आपके सत्यापन अधिकारी यहीं जुड़ेंगे। पेज छोड़ने से आपका सत्र समाप्त हो सकता है।',
  'droom.support.hint.complete': 'यहाँ और कुछ नहीं करना है। आप यह पेज बंद कर सकते हैं।',
  'droom.support.hint.generic':
    'बेहतर इंटरनेट पर फिर कोशिश करें, या कोई दूसरा नेटवर्क आज़माएँ।',

  // ── shared actions & chrome ────────────────────────────────────────────────
  'droom.action.continue': 'आगे बढ़ें',
  'droom.action.tryAgain': 'फिर कोशिश करें',
  'droom.action.retake': 'दोबारा लें',
  'droom.action.checking': 'जाँच हो रही है…',
  'droom.action.skipStep': 'यह चरण छोड़ें',
  'droom.action.pleaseWait': 'कृपया प्रतीक्षा करें…',
  'droom.action.copyLink': 'सत्यापन लिंक कॉपी करें',
  'droom.action.linkCopied': 'लिंक कॉपी हो गया ✓',
  'droom.action.allowed': 'मैंने अनुमति दे दी है',
  'droom.pill.secure': 'सुरक्षित',
  'droom.pill.encrypted': 'एन्क्रिप्टेड',
  // 'सत्यापित' (verified) is a verdict this screen cannot make — it reports submission.
  'droom.pill.verified': 'जमा किया गया',
  'droom.pill.queue': 'लाइव कतार',
  'droom.pill.preparing': 'तैयारी जारी',
  'droom.poweredBy': 'KYC Hub द्वारा संचालित',
  'droom.error.tryAgain': 'कृपया फिर कोशिश करें।',

  // ── app shell ──────────────────────────────────────────────────────────────
  'droom.shell.preparing': 'आपका सत्र तैयार हो रहा है…',
  'droom.shell.entryError.title': 'हम आपका सत्र नहीं खोल पाए',
  'droom.shell.entryError.body':
    'कृपया वही सुरक्षित आमंत्रण लिंक खोलें जो हमने आपको (SMS या ईमेल से) भेजा है। अगर आप पहले से कॉल में थे, तो वही लिंक दोबारा खोलकर वापस जुड़ें।',
  'droom.shell.entryError.retry': 'फिर से खोलें',
  'droom.shell.planError.title': 'हम आपके सत्यापन के चरण लोड नहीं कर पाए',
  'droom.shell.planError.body':
    'हम यह पुष्टि नहीं कर पाए कि इस सत्यापन के लिए क्या ज़रूरी है। कृपया अपना कनेक्शन जाँचें और फिर से प्रयास करें — सही चरण लोड होने तक हम शुरू नहीं करेंगे।',
  'droom.shell.planError.retry': 'फिर से प्रयास करें',

  // ── WELCOME ────────────────────────────────────────────────────────────────
  'droom.welcome.title': 'आइए आपकी पहचान\nसत्यापित करें',
  'droom.welcome.sub':
    'आपका खाता खोलने के लिए एक छोटी, सुरक्षित वीडियो जाँच। इसमें लगभग 5 मिनट लगते हैं।',
  'droom.welcome.trust.rbi.l': 'RBI V-CIP',
  'droom.welcome.trust.ifsca.l': 'IFSCA V-CIP',
  'droom.welcome.trust.tenant.l': 'लाइव वीडियो जाँच',
  'droom.welcome.trust.tenant.s': 'अधिकारी के साथ',
  'droom.welcome.trust.rbi.s': 'विनियमित',
  'droom.welcome.trust.enc.l': 'एन्क्रिप्टेड',
  'droom.welcome.trust.enc.s': 'एंड-टू-एंड',
  'droom.welcome.trust.time.l': '~5 मिनट',
  'droom.welcome.trust.time.s': 'एक ही बैठक में',
  'droom.welcome.cta': 'शुरू करें',
  'droom.welcome.ref': 'संदर्भ',

  // ── SESSION LANDING ────────────────────────────────────────────────────────
  'droom.landing.subtitle': 'वीडियो सत्यापन',
  'droom.landing.title': 'आपका वीडियो सत्यापन',
  'droom.landing.sub':
    'आपकी पहचान की पुष्टि के लिए एक सत्यापन अधिकारी के साथ छोटी, सुरक्षित वीडियो कॉल। इसमें लगभग पाँच मिनट लगते हैं।',
  'droom.landing.need.head': 'आपको क्या चाहिए होगा',
  'droom.landing.need.id.t': 'एक वैध पहचान दस्तावेज़',
  'droom.landing.need.id.d': 'मूल दस्तावेज़ — अपने पास तैयार रखें।',
  'droom.landing.need.cam.t': 'कैमरा और माइक्रोफ़ोन',
  'droom.landing.need.cam.d': 'आपका ब्राउज़र अनुमति माँगेगा — कृपया Allow दबाएँ।',
  'droom.landing.need.loc.t': 'आपकी लाइव लोकेशन',
  'droom.landing.need.loc.d': 'कॉल शुरू होते समय, आपकी अनुमति से ली जाती है।',
  'droom.landing.need.place.t': 'शांत, अच्छी रोशनी वाली जगह',
  'droom.landing.need.place.d': 'ताकि अधिकारी आपको साफ़ देख और सुन सकें।',
  'droom.landing.happen.head': 'क्या होगा',
  'droom.landing.happen.p1':
    'आप एक सत्यापन अधिकारी के साथ **रिकॉर्ड होने वाली** वीडियो कॉल से जुड़ेंगे, जो आपका पहचान दस्तावेज़ जाँचेंगे और कुछ सवाल पूछेंगे। आपकी अनुमति से, कॉल के दौरान आपकी लाइव लोकेशन ली जाती है।',
  'droom.landing.happen.p2':
    'रिकॉर्डिंग, आपकी लोकेशन और आपके जवाब आपके सत्यापन रिकॉर्ड का हिस्सा बनते हैं, जिन्हें नियमों के अनुसार रखा जाता है। कुछ भी लेने से पहले, हर अनुमति आपको स्क्रीन पर समझाई जाएगी।',
  'droom.landing.band.checking.t': 'आपका सत्र जाँचा जा रहा है…',
  'droom.landing.band.checking.b': 'एक पल — आपके सत्यापन प्रदाता से इस लिंक की पुष्टि हो रही है।',
  'droom.landing.band.preparing.t': 'कॉल से पहले कुछ चरण',
  'droom.landing.band.preparing.b':
    'अभी शुरू करें — आप सहमति की पुष्टि करेंगे, अपने ID और selfie की फ़ोटो लेंगे, फिर हम आपको सत्यापन अधिकारी से जोड़ेंगे।',
  'droom.landing.band.joinable.t': 'आप जब तैयार हों',
  'droom.landing.band.joinable.b': 'आपका सत्यापन सत्र जुड़ने के लिए तैयार है।',
  'droom.landing.cta.join': 'अपनी वीडियो कॉल से जुड़ें',
  'droom.landing.cta.start': 'अपना सत्यापन शुरू करें',
  'droom.landing.session': 'सत्र',
  'droom.landing.footer':
    'यह निजी लिंक आपके सत्यापन प्रदाता ने आपको भेजा है — कृपया इसे किसी से साझा न करें।',
  'droom.landing.ended.t': 'यह सत्यापन सत्र समाप्त हो चुका है',
  'droom.landing.ended.b':
    'यहाँ अब जुड़ने को कुछ नहीं है। अगर आपका सत्यापन अभी पूरा करना बाकी है, तो कृपया अपने प्रदाता से नया लिंक माँगें।',
  'droom.landing.notfound.t': 'हम यह सत्यापन सत्र नहीं खोज पाए',
  'droom.landing.notfound.b':
    'देख लें कि आपने आमंत्रण का पूरा लिंक खोला है — या अपने सत्यापन प्रदाता से नया लिंक भेजने को कहें।',

  // ── SCHEDULE ───────────────────────────────────────────────────────────────
  'droom.schedule.subtitle': 'समय चुनें',
  'droom.schedule.aria': 'अपने सत्यापन का समय चुनें',
  'droom.schedule.checking': 'आपका अपॉइंटमेंट जाँचा जा रहा है…',
  'droom.schedule.booked.title': 'आपका अपॉइंटमेंट',
  'droom.schedule.booked.sub':
    'समय आपके टाइमज़ोन ({tz}) में दिखाया गया है। जुड़ना आपके स्लॉट से थोड़ी देर पहले खुलता है — तैयारी के चरण आप अभी पूरे कर सकते हैं।',
  'droom.schedule.booked.continue': 'तैयारी जारी रखें',
  'droom.schedule.booked.move': 'यह अपॉइंटमेंट बदलें…',
  'droom.schedule.held.title': 'समय रोक लिया गया',
  'droom.schedule.held.sub': 'यह रोक लगभग 5 मिनट रहती है — समय पक्का करने के लिए पुष्टि करें।',
  'droom.schedule.held.confirm': 'यह समय पक्का करें',
  'droom.schedule.held.confirming': 'पुष्टि हो रही है…',
  'droom.schedule.held.different': 'कोई और समय चुनें',
  'droom.schedule.pick.title': 'समय चुनें',
  'droom.schedule.pick.empty':
    'अगले 3 दिनों में कोई समय खाली नहीं है — आप चाहें तो अभी आगे बढ़ सकते हैं।',
  'droom.schedule.pick.now': 'अभी आगे बढ़ें',
  'droom.schedule.pick.skip': 'छोड़ें — अभी आगे बढ़ें',
  'droom.schedule.moved.title': 'आपका सत्र आगे बढ़ चुका है',
  'droom.schedule.moved.sub': 'इस सत्र के लिए समय चुनना बंद हो गया है — अगले चरणों पर बढ़ें।',
  'droom.schedule.choice.title': 'आप सत्यापन कब करना चाहेंगे?',
  'droom.schedule.choice.sub':
    'वीडियो सत्यापन में लगभग 10 मिनट लगते हैं। अभी करें, या अपनी सुविधा का समय चुनें।',
  'droom.schedule.choice.pick': 'समय चुनना चाहूँगा/चाहूँगी',
  'droom.schedule.note.taken': 'वह समय अभी-अभी किसी और ने ले लिया — दूसरा चुनें।',
  'droom.schedule.note.holdfail': 'वह समय रोकने में कुछ गड़बड़ हुई — फिर से चुनें।',
  'droom.schedule.note.lapsed': 'आपकी रोक (~5 मिनट) समाप्त हो गई — फिर से समय चुनें।',
  'droom.schedule.note.confirmfail': 'पुष्टि नहीं हो सकी — फिर से समय चुनें।',
  'droom.schedule.note.repick':
    'नया समय चुनें — नए समय की पुष्टि होने तक आपका मौजूदा स्लॉट बना रहेगा।',
  'droom.schedule.note.loadfail': 'समय की सूची नहीं मिल पाई — फिर कोशिश करें।',

  // ── CONSENT (VCIP-REG review set) ──────────────────────────────────────────
  'droom.consent.subtitle': 'Video-KYC',
  'droom.consent.title': 'आपकी निजता, आपकी सहमति',
  'droom.consent.sub':
    'हम आपकी जानकारी का उपयोग केवल {regime} Video-KYC के लिए आपके सत्यापन में करेंगे। यहाँ साफ़-साफ़ बताया है कि क्या और क्यों।',
  'droom.consent.process.head': 'हम क्या प्रोसेस करते हैं',
  'droom.consent.version.title': 'सहमति सूचना का संस्करण',
  'droom.consent.version.unratified': 'शब्दावली को अनुपालन टीम की स्वीकृति मिलनी बाकी है',
  'droom.consent.unratified': 'सूचना की शब्दावली को अनुपालन स्वीकृति मिलनी बाकी है ({version})।',
  'droom.consent.readFull': 'पूरी सूचना पढ़ें',
  'droom.consent.affirm':
    'मैं ऊपर बताए उद्देश्यों के लिए KycHub द्वारा अपने पहचान डेटा के प्रोसेस किए जाने की **सहमति देता/देती हूँ**, ताकि {tenantName} का Video-KYC सत्यापन पूरा हो सके।',
  'droom.consent.agree': 'सहमत हूँ, आगे बढ़ें',
  'droom.consent.recording': 'आपकी सहमति दर्ज हो रही है…',
  'droom.consent.recorded': 'सहमति दर्ज हुई ✓',
  'droom.consent.notNow': 'अभी नहीं',
  'droom.consent.decline': 'मैं सहमति नहीं देता/देती',
  'droom.consent.declined.title': 'आगे बढ़ने के लिए सहमति ज़रूरी है',
  'droom.consent.declined.body':
    '{tenantName} का Video-KYC एक विनियमित सत्यापन है — इसके लिए आपके पहचान डेटा को प्रोसेस करने की आपकी सहमति के बिना हम आगे नहीं बढ़ सकते। आपकी सहमति के बिना कुछ नहीं होता, और आप अपनी सहमति वापस ले सकते हैं या अनिवार्य प्रतिधारण अवधि के बाद अपना डेटा मिटाने का अनुरोध कर सकते हैं।',
  'droom.consent.declined.review': 'सहमति फिर से देखें',
  'droom.consent.loading': 'आपकी सहमति सूचना लोड हो रही है…',
  'droom.consent.loadfail': 'सहमति सूचना लोड नहीं हो पाई',
  'droom.consent.resolved': 'सहमति दर्ज हो गई — आगे बढ़ रहे हैं…',
  'droom.consent.stampline': 'समय-अंकित · IP-बद्ध · hash-लॉग्ड · अपरिवर्तनीय',
  'droom.consent.purpose.video.t': 'रिकॉर्ड होने वाली वीडियो कॉल',
  'droom.consent.purpose.video.d':
    'यह सत्यापन कॉल रिकॉर्ड होती है और आपके KYC साक्ष्य के रूप में रखी जाती है।',
  'droom.consent.purpose.bio.t': 'चेहरा और जीवंतता',
  'droom.consent.purpose.bio.d':
    'आपके चेहरे की लाइव जाँच, आपके पहचान दस्तावेज़ से मिलान के साथ।',
  'droom.consent.purpose.loc.t': 'लाइव लोकेशन',
  'droom.consent.purpose.loc.d':
    'एक बार ली जाती है, यह पुष्टि करने के लिए कि आप किस क्षेत्राधिकार में हैं — जैसा नियामक अपेक्षा करता है।',
  'droom.consent.purpose.ret.t': 'डेटा प्रतिधारण',
  'droom.consent.purpose.ret.d':
    'आपका पहचान डेटा अनिवार्य अवधि तक रखा जाता है और कभी बेचा नहीं जाता।',
  'droom.consent.purpose.dedup.t': 'धोखाधड़ी जाँच',
  'droom.consent.purpose.dedup.d':
    'पहचान की धोखाधड़ी रोकने के लिए, आपके चेहरे के डेटा की तुलना इसी संस्था के अन्य सत्यापन प्रयासों से की जा सकती है। इसे किसी अन्य संस्था के साथ कभी साझा नहीं किया जाता।',

  // ── consent-required interstitials ─────────────────────────────────────────
  'droom.consentreq.title': 'पहले आपकी सहमति चाहिए',
  'droom.consentreq.body.docs':
    'इस सत्यापन के लिए आपकी सहमति का रिकॉर्ड नहीं मिला, इसलिए हम अभी आपके दस्तावेज़ नहीं ले सकते। कृपया सहमति देखकर पुष्टि करें — आप सीधे यहीं वापस आ जाएँगे।',
  'droom.consentreq.body.photo':
    'इस सत्यापन के लिए आपकी सहमति का रिकॉर्ड नहीं मिला, इसलिए हम अभी आपकी फ़ोटो नहीं ले सकते। कृपया सहमति देखकर पुष्टि करें — आप सीधे यहीं वापस आ जाएँगे।',
  'droom.consentreq.review': 'सहमति देखें',

  // ── PAN ────────────────────────────────────────────────────────────────────
  'droom.pan.subtitle': 'PAN सत्यापन',
  'droom.pan.title': 'आपके PAN का विवरण',
  'droom.pan.sub':
    'आपकी वीडियो कॉल से पहले हम आपका PAN जारीकर्ता स्रोत से जाँच लेते हैं — कैमरे पर एक काम कम। परिणाम की पुष्टि आपके सत्यापन अधिकारी कॉल पर करेंगे।',
  'droom.pan.checked.title': 'PAN जाँच लिया गया ✓',
  'droom.pan.checked.sub':
    'जारीकर्ता स्रोत से जाँच हो गई। इसकी पुष्टि आपके सत्यापन अधिकारी कॉल पर करेंगे।',
  'droom.pan.checked.sub.masked':
    'जारीकर्ता स्रोत से जाँच हो गई ({masked})। इसकी पुष्टि आपके सत्यापन अधिकारी कॉल पर करेंगे।',
  'droom.pan.label.pan': 'PAN (जैसा आपके कार्ड पर छपा है)',
  'droom.pan.label.name': 'नाम, बिल्कुल वैसा जैसा PAN कार्ड पर है',
  'droom.pan.err.format':
    'PAN में 5 अक्षर, 4 अंक, फिर एक अक्षर होता है — जैसे AAAAA9999A। कृपया जाँचकर फिर कोशिश करें।',
  'droom.pan.err.mismatch':
    'स्रोत पर यह नाम इस PAN से मेल नहीं खाया। अपने कार्ड से वर्तनी मिलाएँ — या आगे बढ़ें, आपके अधिकारी कॉल पर आपके साथ इसकी पुष्टि करेंगे।',
  'droom.pan.err.mismatch.score':
    'स्रोत पर यह नाम इस PAN से मेल नहीं खाया (मिलान {score}%)। अपने कार्ड से वर्तनी मिलाएँ — या आगे बढ़ें, आपके अधिकारी कॉल पर आपके साथ इसकी पुष्टि करेंगे।',
  'droom.pan.err.invalid':
    'जारीकर्ता स्रोत ने यह PAN नहीं पहचाना। कृपया नंबर दोबारा जाँचें — या आगे बढ़ें, आपके अधिकारी कॉल पर आपके साथ इसकी पुष्टि करेंगे।',
  'droom.pan.err.provider':
    'सत्यापन स्रोत से अभी संपर्क नहीं हो पाया। आप फिर कोशिश कर सकते हैं — या आगे बढ़ें, आपके अधिकारी कॉल पर आपके साथ आपके PAN की पुष्टि करेंगे।',
  'droom.pan.check': 'मेरा PAN जाँचें',
  'droom.pan.checking': 'जाँच हो रही है…',
  'droom.pan.skip': 'अभी छोड़ें — कॉल पर सत्यापित करें',
  'droom.pan.fallback': 'आगे बढ़ें — मेरा PAN कॉल पर अधिकारी के साथ सत्यापित करें',
  'droom.pan.privacy':
    'आपका PAN एक बार, एन्क्रिप्ट करके, जारीकर्ता स्रोत से जाँच के लिए भेजा जाता है। यह आपकी स्वीकृत सूचना के अंतर्गत सत्यापन साक्ष्य के रूप में रखा जाता है, और स्क्रीन पर फिर कभी नहीं दिखाया जाता।',
  'droom.pan.oncall.title': 'आपका PAN हम कॉल पर जाँचेंगे',
  'droom.pan.oncall.body':
    'यह चरण अभी यहाँ उपलब्ध नहीं है — आपके सत्यापन अधिकारी वीडियो कॉल पर आपके साथ आपका PAN जाँचेंगे। कुछ भी छूटा नहीं है, और यहाँ अब कुछ और नहीं करना है।',

  // ── AADHAAR ────────────────────────────────────────────────────────────────
  'droom.aadhaar.subtitle': 'Aadhaar सत्यापन',
  'droom.aadhaar.aria': 'DigiLocker के माध्यम से Aadhaar सत्यापन',
  'droom.aadhaar.relaying': 'आपका Aadhaar सत्यापन पूरा हो रहा है…',
  'droom.aadhaar.checking': 'आपकी सत्यापन स्थिति जाँची जा रही है…',
  'droom.aadhaar.verified.title': 'Aadhaar सत्यापित ✓',
  'droom.aadhaar.verified.sub':
    'आपका Aadhaar DigiLocker के माध्यम से सत्यापित हो गया। यहाँ और कुछ नहीं करना है।',
  'droom.aadhaar.unavailable.title': 'अभी उपलब्ध नहीं है',
  'droom.aadhaar.unavailable.detail':
    'DigiLocker से Aadhaar अभी उपलब्ध नहीं है। आगे बढ़ें — आपके दस्तावेज़ कॉल पर आपके साथ सत्यापित होंगे।',
  'droom.aadhaar.fallback.cta': 'आगे बढ़ें — कॉल पर मेरे साथ सत्यापित करें',
  'droom.aadhaar.fallback.retry': 'DigiLocker फिर आज़माएँ',
  'droom.aadhaar.fallback.error': 'हम अभी इसकी पुष्टि नहीं कर पाए — कृपया थोड़ी देर में फिर कोशिश करें।',
  'droom.aadhaar.fallback.refused':
    'हम इसे कॉल पर नहीं ले जा सके — DigiLocker अभी चालू लग रहा है, कृपया उसे फिर आज़माएँ।',
  'droom.aadhaar.failed.expired': 'वह DigiLocker प्रयास समाप्त हो गया — कृपया दोबारा शुरू करें।',
  'droom.aadhaar.failed.title': 'यह पूरा नहीं हो पाया',
  'droom.aadhaar.failed.incomplete': 'सत्यापन पूरा नहीं हुआ।',
  'droom.aadhaar.failed.relay':
    'सत्यापन पूरा नहीं हो सका — आप फिर कोशिश कर सकते हैं, या इसके बिना आगे बढ़ सकते हैं।',
  'droom.aadhaar.failed.state':
    'यह चरण अभी नहीं चल पा रहा। फिर कोशिश करें — या आपके अधिकारी कॉल पर आपके साथ आपके दस्तावेज़ की पुष्टि करेंगे।',
  'droom.aadhaar.failed.start':
    'DigiLocker की प्रक्रिया शुरू नहीं हो सकी — फिर कोशिश करें, या इसके बिना आगे बढ़ें।',
  'droom.aadhaar.offer.title': 'DigiLocker से अपना Aadhaar सत्यापित करें',
  'droom.aadhaar.offer.body':
    'आपको सरकार की DigiLocker सेवा पर ले जाया जाएगा, जहाँ आप अपने Aadhaar और OTP से साइन इन करेंगे। यह उन्हीं की साइट पर होता है — हम आपका Aadhaar नंबर कभी नहीं देखते, और यहाँ केवल सत्यापन का परिणाम वापस आता है।',
  'droom.aadhaar.label.name': 'आपका नाम (जैसा Aadhaar पर है)',
  'droom.aadhaar.placeholder.name': 'नाम',
  'droom.aadhaar.label.mobile': 'मोबाइल नंबर',
  'droom.aadhaar.placeholder.mobile': '10 अंकों का मोबाइल',
  'droom.aadhaar.err.mobile': 'मान्य भारतीय मोबाइल नंबर लिखें (शुरुआत 6, 7, 8 या 9 से होनी चाहिए)।',
  'droom.aadhaar.err.form': 'कृपया अपना मोबाइल नंबर जाँचकर फिर कोशिश करें।',
  'droom.aadhaar.hint':
    'आपका नाम और मोबाइल केवल DigiLocker साइन-इन शुरू करते हैं — आपका सत्यापित विवरण DigiLocker से ही वापस आता है।',
  'droom.aadhaar.cta': 'DigiLocker से सत्यापित करें',
  'droom.aadhaar.starting': 'शुरू हो रहा है…',
  'droom.aadhaar.skip': 'छोड़ें — दस्तावेज़ कॉल पर सत्यापित करें',
  'droom.aadhaar.without': 'इसके बिना आगे बढ़ें',

  // ── CAPTURE (legacy pair) ──────────────────────────────────────────────────
  'droom.capture.subtitle': 'चरण 2 · फ़ोटो',
  'droom.capture.doc.label': 'पहचान दस्तावेज़',
  'droom.capture.doc.title': 'अपने पहचान दस्तावेज़ की फ़ोटो लें',
  'droom.capture.doc.sub':
    'दस्तावेज़ को सीधा रखें, पूरे फ़्रेम में लाएँ, और चमक से बचें — हर विवरण पढ़ा जा सकना चाहिए।',
  'droom.capture.selfie.label': 'Selfie',
  'droom.capture.selfie.title': 'अब एक साफ़ selfie',
  'droom.capture.selfie.sub':
    'समान रोशनी में कैमरे की ओर देखें और अपना पूरा चेहरा घेरे के अंदर रखें।',
  'droom.capture.loading.title': 'आपकी फ़ोटो की तैयारी हो रही है',
  'droom.capture.loading.sub': 'देखा जा रहा है कि आप पहले क्या अपलोड कर चुके हैं।',
  'droom.capture.snag': 'कुछ अटक गया',
  'droom.capture.complete': 'फ़ोटो पूरी हुईं',
  'droom.capture.queue.entering': 'आपको सत्यापन कतार में जोड़ा जा रहा है…',
  'droom.capture.queue.failed': 'हम आपको कतार में नहीं जोड़ पाए — कृपया फिर कोशिश करें।',
  'droom.capture.moment': 'एक पल…',
  'droom.capture.err.link':
    'यह सत्यापन लिंक अब मान्य नहीं है — कृपया आपको भेजा गया सबसे नया लिंक इस्तेमाल करें।',
  'droom.capture.err.list': 'हम आपके लिए गए दस्तावेज़ नहीं जाँच पाए। कृपया फिर कोशिश करें।',
  'droom.capture.err.queue':
    'हम आपको कतार में नहीं जोड़ पाए। कृपया अपना इंटरनेट जाँचकर फिर कोशिश करें।',
  'droom.capture.flip': 'कैमरा बदलें',
  'droom.capture.badge.rear': 'पीछे का कैमरा',
  'droom.capture.badge.front': 'सामने का कैमरा',
  'droom.capture.badge.camera': 'कैमरा',
  'droom.capture.badge.preview': 'पूर्वावलोकन',
  'droom.capture.retakeThis': 'कृपया यह फ़ोटो दोबारा लें।',
  'droom.capture.primer.rear': 'आपके दस्तावेज़ की फ़ोटो के लिए हम पीछे का कैमरा इस्तेमाल करेंगे।',
  'droom.capture.hint.face': 'अपना पूरा चेहरा घेरे के अंदर रखें',
  'droom.capture.hint.doc': 'दस्तावेज़ को पूरे फ़्रेम में रखें · चमक से बचें',
  'droom.capture.session.checking': 'आपका सत्र जाँचा जा रहा है…',
  'droom.capture.session.loadfail': 'लोड नहीं हो पाया',
  'droom.capture.use': 'यह फ़ोटो इस्तेमाल करें',
  'droom.capture.turnon': 'कैमरा चालू करें',
  'droom.capture.shoot': 'फ़ोटो लें',
  'droom.capture.assessing.short': 'गुणवत्ता जाँची जा रही है…',
  'droom.capture.camera.startingBtn': 'कैमरा शुरू हो रहा है…',
  'droom.capture.camera.checkingBtn': 'कैमरा जाँचा जा रहा है…',
  'droom.capture.queue.enteringBtn': 'कतार में जोड़ा जा रहा है…',
  'droom.capture.tile.uploadedEarlier': 'पहले अपलोड हो चुका',
  'droom.capture.tile.capturedEarlier': 'पहले ली जा चुकी',
  'droom.capture.tile.uploading': 'अपलोड हो रहा है…',
  'droom.capture.tile.inprogress': 'चल रहा है',
  'droom.capture.tile.next': 'अगला',
  'droom.capture.tile.hash': 'सर्वर-साइड अखंडता hash (पहले 12 अक्षर)',
  'droom.capture.footer': 'अपलोड से पहले गुणवत्ता आपके डिवाइस पर ही जाँची जाती है',
  'droom.capture.previewAlt': 'आपकी ली गई फ़ोटो का पूर्वावलोकन',
  'droom.capture.doc.titleTyped': 'अपने {doc} की फ़ोटो लें',

  // ── selfie-family configs ──────────────────────────────────────────────────
  'droom.selfiestep.subtitle': 'Selfie',
  'droom.selfiestep.tile': 'Selfie',
  'droom.selfiedocstep.subtitle': 'दस्तावेज़ के साथ फ़ोटो',
  'droom.selfiedocstep.tile': 'आप + दस्तावेज़',
  'droom.selfiecore.loading.title': 'आपका कैमरा तैयार हो रहा है',
  'droom.selfiecore.loading.sub': 'देखा जा रहा है कि यह फ़ोटो पहले हो चुकी है या नहीं।',
  'droom.selfiecore.notavailable': 'यहाँ अभी उपलब्ध नहीं है',

  // ── DOCUMENT UPLOAD ────────────────────────────────────────────────────────
  'droom.uploadstep.subtitle': 'दस्तावेज़',
  'droom.uploadstep.none':
    'यहाँ आपको कुछ अपलोड नहीं करना है — आपके दस्तावेज़ कॉल पर आपके सत्यापन अधिकारी के साथ जाँचे जाते हैं।',
  'droom.uploadstep.notyet': 'अभी अपलोड नहीं हुआ',
  'droom.uploadstep.oncall': 'आपकी कॉल पर होगा',
  'droom.uploadstep.lockedCta': 'आगे बढ़ने के लिए अपना {doc} अपलोड करें',
  'droom.uploadstep.uploadingAria': 'आपका {doc} अपलोड हो रहा है',
  'droom.uploadstep.footer': 'अपलोड से पहले फ़ाइलें आपके डिवाइस पर ही जाँची जाती हैं',
  'droom.doctype.PAN_CARD': 'PAN कार्ड',
  'droom.doctype.AADHAAR': 'Aadhaar',
  'droom.doctype.DRIVING_LICENCE': 'Driving licence',
  'droom.doctype.VOTER_ID': 'Voter ID',
  'droom.doctype.PASSPORT': 'Passport',
  'droom.doctype.UTILITY_BILL': 'यूटिलिटी बिल',
  'droom.doctype.SELFIE': 'Selfie',
  'droom.doctype.SELFIE_WITH_DOC': 'दस्तावेज़ के साथ selfie',
  'droom.doctype.OTHER_DOCUMENT': 'सहायक दस्तावेज़',
  'droom.doctype.fallback': 'दस्तावेज़',

  // ── stepper step labels (item 7) ───────────────────────────────────────────
  'droom.step.consent': 'सहमति',
  'droom.step.capture': 'दस्तावेज़ और selfie',
  'droom.step.confirm': 'अपने विवरण की पुष्टि',
  'droom.step.waiting': 'प्रतीक्षा कक्ष',
  'droom.step.call': 'लाइव वीडियो कॉल',
  'droom.step.complete': 'सब हो गया',

  // ── MissingCheckStep ───────────────────────────────────────────────────────
  'droom.check.DIGILOCKER_AADHAAR': 'Aadhaar (DigiLocker)',
  'droom.check.PAN_SOURCE': 'PAN सत्यापन',
  'droom.check.DOC_CAPTURE': 'दस्तावेज़ की फ़ोटो',
  'droom.check.CONSENT': 'सहमति',
  'droom.check.FACE_MATCH': 'चेहरे का मिलान',
  'droom.check.LIVENESS': 'जीवंतता',
  'droom.check.GEO_IP': 'लोकेशन जाँच',
  'droom.check.AGENT_QUESTIONS': 'अधिकारी के सवाल',
  'droom.check.AML_SCREENING': 'स्क्रीनिंग',
  'droom.check.SELFIE': 'Selfie',
  'droom.check.SELFIE_WITH_DOC': 'दस्तावेज़ के साथ फ़ोटो',
  'droom.missing.aria': '{label} — कॉल पर होगा',
  'droom.missing.body':
    'यह चरण लाइव कॉल पर आपके सत्यापन अधिकारी के साथ पूरा होता है — यहाँ अभी कुछ नहीं करना। अगले चरण पर बढ़ें।',

  // ── WAITING ROOM ───────────────────────────────────────────────────────────
  'droom.wait.subtitle': 'प्रतीक्षा कक्ष',
  'droom.wait.officerJoined': 'आपके सत्यापन अधिकारी जुड़ गए हैं — आपको अभी जोड़ा जा रहा है',
  'droom.wait.finding': 'कतार में आपकी जगह खोजी जा रही है…',
  'droom.wait.next': 'अगली बारी आपकी है — अधिकारी के खाली होते ही हम आपको जोड़ देंगे',
  'droom.wait.position': 'अगले सत्यापन अधिकारी के लिए कतार में आपका स्थान {position} है',
  'droom.wait.eta': 'अनुमानित प्रतीक्षा ~{min} मिनट',
  'droom.wait.progressAria': 'कतार की प्रगति',
  'droom.wait.keepOpen': 'यह स्क्रीन खुली रखें',
  'droom.wait.autoconnect': 'अधिकारी के जुड़ते ही हम आपको जोड़ देंगे',
  'droom.wait.spotSaved': 'आपकी जगह सुरक्षित है',
  'droom.wait.reschedule': 'बाद के लिए समय बदलें',
  'droom.wait.rescheduling': 'समय-सूची खुल रही है…',
  'droom.wait.refusal.title': 'हम अभी आपको कतार में नहीं जोड़ सके',
  'droom.wait.refusal.body': 'पिछले किसी चरण का एक काम अभी बाकी है:',
  'droom.wait.refusal.goback': 'वापस जाकर पूरा करें',

  // ── SCHEDULED ARRIVAL (CD32) ───────────────────────────────────────────────
  'droom.arrival.subtitle': 'आपका अपॉइंटमेंट',
  'droom.arrival.title': 'आपका सत्यापन निर्धारित है',
  'droom.arrival.ist': '{time} IST',
  'droom.arrival.body':
    'आप समय से पहले आ गए हैं — कोई गड़बड़ नहीं है। आपके अपॉइंटमेंट का समय आते ही हम आपको अपने आप कतार में ले आएँगे।',
  'droom.arrival.countdown': '{time} में खुलेगा',
  'droom.arrival.countdownAria': 'आपका अपॉइंटमेंट खुलने में बचा समय',
  'droom.arrival.autoconnect': 'हम आपको अपने आप जोड़ देंगे',
  'droom.arrival.stepsSaved': 'आपके पूरे किए गए चरण सुरक्षित हैं',
  'droom.arrival.missed.title': 'आपका निर्धारित समय निकल चुका है',
  'droom.arrival.missed.was': 'आपका अपॉइंटमेंट {time} था।',
  'droom.arrival.missed.body':
    'उस समय में हम आपको जोड़ नहीं सके, इसलिए नया समय चुनना होगा। आपने अब तक जो पूरा किया है, वह सुरक्षित रहेगा।',
  'droom.arrival.missed.cta': 'नया समय चुनें',

  // ── gates ──────────────────────────────────────────────────────────────────
  'droom.live.subtitle': 'लाइव सत्यापन',
  'droom.geo.explain.title': 'अपनी लाइव लोकेशन साझा करें',
  'droom.geo.explain.body':
    'इस सत्यापन में आपकी लाइव लोकेशन शामिल है — आपकी संस्था की प्रक्रिया, RBI V-CIP मार्गदर्शन के अनुरूप, इस वीडियो कॉल के दौरान पुष्टि करती है कि आप कहाँ हैं। यह एक बार, आपकी अनुमति से ली जाती है, और आपके सत्यापन रिकॉर्ड का हिस्सा बनती है।',
  'droom.geo.explain.body.tenant':
    'इस सत्यापन में आपकी लाइव लोकेशन शामिल है — आपकी संस्था की प्रक्रिया इस वीडियो कॉल के दौरान पुष्टि करती है कि आप किस देश से कॉल कर रहे हैं। यह एक बार, आपकी अनुमति से ली जाती है, और आपके सत्यापन रिकॉर्ड का हिस्सा बनती है।',
  'droom.geo.explain.body.ifsca':
    'इस सत्यापन में आपकी लाइव लोकेशन शामिल है — आपकी संस्था की प्रक्रिया, IFSCA KYC मार्गदर्शन के अनुरूप, इस वीडियो कॉल के दौरान पुष्टि करती है कि आप किस क्षेत्राधिकार (jurisdiction) से कॉल कर रहे हैं। यह एक बार, आपकी अनुमति से ली जाती है, और आपके सत्यापन रिकॉर्ड का हिस्सा बनती है।',
  'droom.geo.explain.cta': 'मेरी लोकेशन साझा करें',
  'droom.geo.captured.title': 'लोकेशन दर्ज हो गई',
  'droom.geo.getting.title': 'आपकी लोकेशन ली जा रही है…',
  'droom.geo.captured.body': 'धन्यवाद — आपको आपके सत्यापन अधिकारी से जोड़ा जा रहा है।',
  'droom.geo.getting.body': 'अगर आपका ब्राउज़र पूछे, तो कृपया Allow दबाएँ।',
  'droom.geo.denied.title': 'लोकेशन की अनुमति नहीं मिली',
  'droom.geo.denied.body':
    'कॉल फिर भी हो सकती है — आपके सत्यापन अधिकारी देखेंगे कि लोकेशन का साक्ष्य नहीं है, और इस बारे में पूछ सकते हैं। साझा करने के लिए, ब्राउज़र सेटिंग में इस साइट के लिए लोकेशन की अनुमति दें, फिर दोबारा कोशिश करें।',
  // DEC-060(A): ठोस कदम — अधिकारी अपनी स्क्रीन पर यही मार्गदर्शन देखते हैं, ताकि दोनों साथ मिलकर
  // गलती से दबे "Block" को ठीक कर सकें।
  'droom.geo.denied.howto':
    'अनुमति ऐसे दें: पता बार के पास ताले (या ⓘ) के निशान पर टैप करें → Permissions → Location → Allow, फिर "फिर से कोशिश करें" दबाएँ। iPhone पर Settings → Privacy & Security → Location Services → अपना ब्राउज़र → While Using the App भी जाँचें। अगर पूरे डिवाइस की लोकेशन बंद है, तो पहले क्विक सेटिंग्स से उसे चालू करें।',
  'droom.geo.without': 'लोकेशन के बिना आगे बढ़ें',
  'droom.geo.postfailed.title': 'आपकी लोकेशन दर्ज नहीं हो पाई',
  'droom.geo.degraded.title': 'हम आपकी लोकेशन नहीं ले पाए',
  'droom.geo.degraded.fallback': 'आपकी लोकेशन लेते समय कुछ गड़बड़ हुई।',
  'droom.geo.degraded.body':
    'आप फिर कोशिश कर सकते हैं, या आगे बढ़ें — आपके अधिकारी देखेंगे कि लोकेशन का साक्ष्य नहीं है।',

  'droom.perm.checking': 'आपका कैमरा और माइक्रोफ़ोन जाँचा जा रहा है…',
  'droom.perm.primer.title': 'अपना कैमरा और माइक्रोफ़ोन चालू करें',
  'droom.perm.primer.body':
    'अधिकारी के साथ लाइव सत्यापन कॉल के लिए आपका कैमरा और माइक्रोफ़ोन ज़रूरी हैं। आपका ब्राउज़र अभी पूछेगा — कृपया **Allow** दबाएँ।',
  'droom.perm.requesting.title': 'आपकी अनुमति की प्रतीक्षा है…',
  'droom.perm.requesting.body': 'अगर आपका ब्राउज़र पूछ रहा है, तो कृपया **Allow** दबाएँ।',
  'droom.perm.denied.title': 'कैमरा और माइक्रोफ़ोन अवरुद्ध हैं',
  'droom.perm.denied.body':
    'आपका ब्राउज़र अनुमति रोक रहा है, और इसे केवल आप ही वापस चालू कर सकते हैं — कोई वेबसाइट आपके लिए दोबारा नहीं पूछ सकती।',
  'droom.perm.osdenied.title': 'सिस्टम सेटिंग में कैमरे की अनुमति दें',
  'droom.perm.osdenied.body':
    'आपका ब्राउज़र अनुमति का संदेश नहीं दिखा पाया — अनुमति सिस्टम स्तर पर बंद है। इसे आपको स्वयं बदलना होगा।',
  'droom.perm.steps.aria': 'अनुमति कैसे दें',
  'droom.perm.autoresume': 'आपके अनुमति देते ही हम अपने-आप फिर जुड़ जाएँगे।',
  'droom.perm.nodevice.title': 'कैमरा या माइक्रोफ़ोन नहीं मिला',
  'droom.perm.nodevice.fallback': 'इस डिवाइस पर कॉल के लिए कैमरा या माइक्रोफ़ोन उपलब्ध नहीं है।',
  'droom.perm.nodevice.advice': 'कृपया चालू कैमरे और माइक्रोफ़ोन वाला डिवाइस इस्तेमाल करें।',
  'droom.perm.webview.title': 'यह लिंक अपने ब्राउज़र में खोलें',
  'droom.perm.webview.body':
    'आपने इसे किसी दूसरे ऐप के अंदर से खोला है, जो कैमरे का भरोसेमंद इस्तेमाल नहीं कर पाता। सत्यापन जारी रखने के लिए कृपया यह लिंक **Chrome** या **Safari** में खोलें।',
  'droom.perm.webview.tip': 'सुझाव: ••• मेनू दबाकर "Open in browser" चुनें।',
  'droom.perm.unsupported.title': 'यह ब्राउज़र कॉल नहीं चला सकता',
  'droom.perm.unsupported.body':
    'आपका ब्राउज़र लाइव कैमरा इस्तेमाल नहीं कर पाता। कृपया यह लिंक नए Chrome या Safari में खोलें — iPhone पर iOS 14.3 या नया; Android पर Chrome 90 या नया।',

  // Testmap #12 — पुराने डिवाइस के लिए ईमानदार सूचना (advisory)।
  'droom.perm.osnotice.ios':
    'आपके iPhone का सॉफ़्टवेयर इस कॉल के लिए पुराना है ({min} या नया चाहिए)। आप कोशिश कर सकते हैं, लेकिन कैमरा न चले तो Settings → General → Software Update से अपडेट करके यह लिंक दोबारा खोलें।',
  'droom.perm.osnotice.android':
    'आपका ब्राउज़र इस कॉल के लिए पुराना है ({min} या नया चाहिए)। आप कोशिश कर सकते हैं, लेकिन कैमरा न चले तो Play Store से Chrome अपडेट करके यह लिंक दोबारा खोलें।',
  'droom.perm.osnotice.samsung':
    'आपका ब्राउज़र इस कॉल के लिए पुराना है ({min} या नया चाहिए)। कृपया Samsung Internet अपडेट करें — या यह लिंक Chrome में खोलें — और फिर कोशिश करें।',

  'droom.perm.rec.os.1': 'आपका ब्राउज़र अनुमति का संदेश नहीं दिखा पाया — रोक सिस्टम स्तर पर है।',
  'droom.perm.rec.os.2':
    'अपने डिवाइस की Settings → Privacy → Camera (और Microphone) खोलकर अपने ब्राउज़र को अनुमति दें।',
  'droom.perm.rec.os.3': 'यहाँ वापस आकर "मैंने अनुमति दे दी है" दबाएँ।',
  'droom.perm.rec.chrome.1': 'एड्रेस बार के बाईं ओर कैमरा / ताले के आइकन पर टैप करें।',
  'droom.perm.rec.chrome.2': 'Camera और Microphone को Allow पर सेट करें।',
  'droom.perm.rec.chrome.3': '"मैंने अनुमति दे दी है" दबाएँ (reload की ज़रूरत नहीं)।',
  'droom.perm.rec.firefox.1':
    'एड्रेस बार में ताले के आइकन पर टैप करें, फिर Connection settings / Permissions चुनें।',
  'droom.perm.rec.firefox.2':
    'अवरुद्ध Camera और Microphone हटाकर उन्हें Allow पर सेट करें।',
  'droom.perm.rec.firefox.3': '"मैंने अनुमति दे दी है" दबाएँ।',
  'droom.perm.rec.safari.1':
    'Safari → इस वेबसाइट की Settings खोलें (या aA मेनू → Website Settings)।',
  'droom.perm.rec.safari.2':
    'Camera और Microphone को Allow पर सेट करें, कहा जाए तो reload करें।',
  'droom.perm.rec.safari.3': '"मैंने अनुमति दे दी है" दबाएँ।',
  'droom.perm.rec.generic.1': 'इस पेज के लिए अपने ब्राउज़र की site settings खोलें।',
  'droom.perm.rec.generic.2': 'Camera और Microphone को Allow पर सेट करें।',
  'droom.perm.rec.generic.3': '"मैंने अनुमति दे दी है" दबाएँ।',

  'droom.cam.denied.title': 'कैमरे की अनुमति अवरुद्ध है',
  'droom.cam.denied.body':
    'आपका ब्राउज़र कैमरा रोक रहा है, और इसे केवल आप ही वापस चालू कर सकते हैं — कोई वेबसाइट आपके लिए दोबारा नहीं पूछ सकती।',
  'droom.cam.osdenied.title': 'सिस्टम सेटिंग में कैमरे की अनुमति दें',
  'droom.cam.osdenied.body':
    'आपका ब्राउज़र अनुमति का संदेश नहीं दिखा पाया — अनुमति सिस्टम स्तर पर बंद है। इसे आपको स्वयं बदलना होगा।',
  'droom.cam.nodevice.title': 'कैमरा नहीं मिला',
  'droom.cam.nodevice.fallback':
    'इस डिवाइस पर कोई काम करने वाला कैमरा नहीं है। कृपया चालू कैमरे वाले डिवाइस पर जारी रखें।',
  'droom.cam.webview.title': 'यह लिंक अपने ब्राउज़र में खोलें',
  'droom.cam.webview.body':
    'आपने इसे किसी दूसरे ऐप के अंदर से खोला है, जो कैमरे का भरोसेमंद इस्तेमाल नहीं कर पाता। जारी रखने के लिए कृपया यह लिंक Chrome या Safari में खोलें।',
  'droom.cam.unsupported.title': 'यह ब्राउज़र फ़ोटो नहीं ले सकता',
  'droom.cam.unsupported.body':
    'यहाँ लाइव कैमरा उपलब्ध नहीं है। कृपया यह लिंक नए Chrome या Safari में खोलें।',
  'droom.cam.nodevice.hookDetail': 'इस डिवाइस पर कोई काम करने वाला कैमरा नहीं मिला।',
  'droom.perm.nodevice.hookDetail': 'इस डिवाइस पर कैमरा या माइक्रोफ़ोन नहीं मिला।',

  // ── LIVE CALL ──────────────────────────────────────────────────────────────
  'droom.call.connectingNamed': 'आपको {name} से जोड़ा जा रहा है…',
  'droom.call.connecting': 'आपको आपके सत्यापन अधिकारी से जोड़ा जा रहा है…',
  'droom.call.verifierAway.title': 'आपके सत्यापन अधिकारी फिर से जुड़ रहे हैं',
  'droom.call.verifierAway.body':
    'कृपया कॉल पर बने रहें — इसमें एक मिनट तक लग सकता है। असुविधा के लिए हमें खेद है।',
  'droom.call.connectingSlow.body':
    'इसमें सामान्य से अधिक समय लग रहा है — कृपया इसी स्क्रीन पर बने रहें।',
  'droom.call.verifierLost.title': 'यह कॉल समाप्त की जा रही है',
  'droom.call.verifierLost.body':
    'सत्यापन फिर से शुरू करने के लिए आपके सत्यापन अधिकारी आपसे दोबारा संपर्क करेंगे। असुविधा के लिए हम क्षमा चाहते हैं।',
  'droom.call.verifierLost.support': 'अब आप यह पेज बंद कर सकते हैं।',
  'droom.call.cameraLost.body':
    'जब आप दूर थे तब आपका कैमरा रुक गया था — इसे फिर से जोड़ा जा रहा है…',
  'droom.call.resumeMedia.cta': 'कैमरा और आवाज़ फिर से चालू करने के लिए टैप करें',
  'droom.call.verifierNamed': '{name} · सत्यापन अधिकारी',
  'droom.call.verifier': 'सत्यापन अधिकारी',
  'droom.call.rec': 'REC · एन्क्रिप्टेड',
  'droom.call.swapTiles': 'व्यू बदलें',
  'droom.call.swapTiles.showVerifier': 'सत्यापन अधिकारी को बड़ा दिखाएँ',
  'droom.call.swapTiles.showMe': 'मुझे बड़ा दिखाएँ',
  'droom.call.guidance.label': 'स्क्रीन पर मार्गदर्शन',
  'droom.call.guidance.text':
    'अपना चेहरा बीच में और अच्छी रोशनी में रखें। जुड़ने के बाद आपके सत्यापन अधिकारी हर चरण में आपका मार्गदर्शन करेंगे।',
  'droom.call.mute': 'माइक्रोफ़ोन म्यूट करें',
  'droom.call.unmute': 'माइक्रोफ़ोन अनम्यूट करें',
  'droom.call.end': 'कॉल समाप्त करें',
  'droom.call.camOff': 'कैमरा बंद करें',
  'droom.call.camOn': 'कैमरा चालू करें',
  'droom.call.leave.title': 'कॉल छोड़ें?',
  'droom.call.leave.body':
    'आपका सत्यापन अभी पूरा नहीं हुआ है। अभी छोड़ेंगे तो आप फिर जुड़कर उसी अधिकारी के साथ जारी रख सकते हैं।',
  'droom.call.leave.stay': 'कॉल पर बने रहें',
  'droom.call.leave.leave': 'कॉल छोड़ें',
  'droom.call.left.title': 'आपने कॉल छोड़ दी',
  'droom.call.left.body':
    'आपका सत्यापन पूरा नहीं हुआ है। जहाँ छोड़ा था वहीं से जारी रखने के लिए फिर जुड़ें — आपके अधिकारी और आपकी प्रगति सुरक्षित हैं।',
  'droom.call.left.rejoin': 'कॉल से फिर जुड़ें',
  'droom.call.superseded.title': 'आपने दूसरे डिवाइस पर जारी रखा है',
  'droom.call.superseded.body':
    'आपका सत्यापन अब दूसरे डिवाइस पर खुला है — वहीं जारी रखें, आपकी प्रगति साथ चलती है। कुछ भी खोया नहीं है, और इस स्क्रीन पर कुछ करने की ज़रूरत नहीं है।',
  'droom.call.expired.title': 'यह सत्र समाप्त हो चुका है',
  'droom.call.expired.body':
    'लिंक की अवधि समाप्त हो गई होगी या यह किसी और डिवाइस पर खोला गया होगा। अगर आपका सत्यापन पूरा नहीं हुआ था, तो कृपया अपने मूल लिंक से दोबारा शुरू करें, या नए लिंक के लिए {tenantName} से संपर्क करें।',

  'droom.rc.lost': 'कनेक्शन टूट गया',
  'droom.rc.reconnecting': 'फिर से जुड़ रहे हैं…',
  'droom.rc.sub.suspended':
    'आपकी प्रगति सुरक्षित है। जब तैयार हों, फिर जुड़ें — आप ठीक वहीं से, उसी सत्यापन अधिकारी के साथ जारी रखेंगे।',
  'droom.rc.sub.blip':
    'आपकी प्रगति सुरक्षित है। कृपया इसी स्क्रीन पर बने रहें — हम ठीक वहीं से शुरू करेंगे जहाँ आप थे।',
  'droom.rc.kept': 'चरण {step} / {total} सुरक्षित',
  'droom.rc.kept.same': 'चरण {step} / {total} सुरक्षित · वही सत्यापन अधिकारी',
  'droom.rc.rejoin': 'अभी फिर जुड़ें',

  // ── guided capture overlay ─────────────────────────────────────────────────
  'droom.gcap.badge': 'सत्यापन अधिकारी का अनुरोध',
  'droom.gcap.facing.rear':
    'आपके सत्यापन अधिकारी ने पिछला कैमरा उपयोग करने को कहा है — यह दस्तावेज़ अधिक स्पष्ट दिखाता है। फ़ोटो लेते समय आपकी कॉल वीडियो बदलेगी और उसके बाद वापस हो जाएगी।',
  'droom.gcap.facing.front':
    'आपके सत्यापन अधिकारी ने इस फ़ोटो के लिए सामने का कैमरा उपयोग करने को कहा है। फ़ोटो लेते समय आपकी कॉल वीडियो बदलेगी और उसके बाद वापस हो जाएगी।',
  'droom.gcap.facing.applied.rear':
    'आपके सत्यापन अधिकारी के अनुरोध पर इस फ़ोटो के लिए पिछला कैमरा उपयोग हो रहा है। फ़ोटो पूरी होते ही आपकी कॉल वीडियो वापस पहले जैसी हो जाएगी।',
  'droom.gcap.facing.applied.front':
    'आपके सत्यापन अधिकारी के अनुरोध पर इस फ़ोटो के लिए सामने का कैमरा उपयोग हो रहा है।',
  'droom.gcap.facing.oneCamera':
    'इस डिवाइस में एक ही कैमरा है, इसलिए हम उसी का उपयोग कर रहे हैं — आप फिर भी फ़ोटो ले सकते हैं।',
  'droom.gcap.facing.unavailable':
    'इस डिवाइस पर कैमरा बदला नहीं जा सका, इसलिए हम मौजूदा कैमरे का उपयोग करेंगे। आप फिर भी फ़ोटो ले सकते हैं।',
  'droom.gcap.facing.unconfirmed':
    'आपका कैमरा बदल गया है, लेकिन यह डिवाइस नहीं बताता कि अब कौन-सा कैमरा उपयोग में है। फ़ोटो लेने से पहले कृपया नीचे दिख रहा प्रीव्यू देख लें।',
  'droom.gcap.purpose.title':
    'यह फ़ोटो लेने से पहले',
  'droom.gcap.purpose.what':
    'हम इस कॉल से आपके चेहरे या आपके दस्तावेज़ की फ़ोटो लेंगे।',
  'droom.gcap.purpose.comparedWith':
    'यह आपके आधार फ़ोटो और आपके दिए पहचान दस्तावेज़ से मिलाई जाती है, ताकि पुष्टि हो कि आप वही हैं।',
  'droom.gcap.purpose.processor':
    'इसे हम और हमारा सत्यापन साझेदार, भारत में ही संभालते हैं।',
  'droom.gcap.purpose.retention':
    'इसे आपके सत्यापन रिकॉर्ड के रूप में तब तक रखा जाता है जब तक कानून अपेक्षा करता है।',
  'droom.gcap.purpose.noTraining':
    'इसका उपयोग किसी AI मॉडल को प्रशिक्षित या बेहतर करने के लिए कभी नहीं होता।',
  'droom.gcap.purpose.withdraw':
    'आप किसी भी समय रुक सकते हैं और कॉल पर अपने सत्यापनकर्ता से बात कर सकते हैं।',
  'droom.gcap.purpose.agree':
    'मैं समझ गया — आगे बढ़ें',
  'droom.gcap.attempts.exhausted':
    'हमें पर्याप्त स्पष्ट फ़ोटो नहीं मिल सकी। कृपया कॉल पर अपने सत्यापनकर्ता से बात करें — वे मदद करेंगे।',
  'droom.gcap.asked': 'आपके सत्यापन अधिकारी ने कॉल पर यह फ़ोटो माँगी है।',
  'droom.gcap.doc.title': 'अपना दस्तावेज़ कैमरे को दिखाएँ',
  'droom.gcap.doc.instruction':
    'दस्तावेज़ को बॉक्स के अंदर सीधा रखें, इतना पास कि हर विवरण पढ़ा जा सके।',
  'droom.gcap.selfiedoc.title': 'अपना दस्तावेज़ चेहरे के पास रखें',
  'droom.gcap.selfiedoc.instruction':
    'एक ही फ़ोटो में आप और आपका दस्तावेज़ साथ दिखें — चेहरा घेरे में, दस्तावेज़ बॉक्स में।',
  'droom.gcap.selfie.title': 'लाइव सेल्फ़ी लें',
  'droom.gcap.selfie.instruction':
    'अच्छी रोशनी में, अपना पूरा चेहरा अंडाकार घेरे में रखते हुए सीधे कैमरे में देखें।',
  'droom.gcap.cat.unclear': 'फ़ाइल में मौजूद कॉपी पढ़ने में कठिन है, इसलिए एक साफ़ फ़ोटो चाहिए।',
  'droom.gcap.cat.mismatch': 'कुछ विवरण आपके दस्तावेज़ से दोबारा मिलाने हैं।',
  'droom.gcap.cat.tamper': 'आपके सत्यापन अधिकारी को असली दस्तावेज़ कैमरे पर लाइव देखना है।',
  'droom.gcap.cat.identity':
    'आपके सत्यापन अधिकारी को दोबारा पुष्टि करनी है कि दस्तावेज़ आपके ही हाथ में है।',
  'droom.gcap.cat.regulatory': 'आपके सत्यापन के लिए यह फ़ोटो रिकॉर्ड होती कॉल पर ज़रूरी है।',
  'droom.gcap.cat.flow': 'यह फ़ोटो आपकी सत्यापन यात्रा का हिस्सा है — आपका वेरिफ़ायर आपका मार्गदर्शन करेगा।',
  'droom.gcap.cat.default': 'आपका सत्यापन जारी रखने के लिए आपके अधिकारी को यह फ़ोटो चाहिए।',
  'droom.gcap.done.title': 'फ़ोटो आपके सत्यापन अधिकारी को भेज दी गई',
  'droom.gcap.done.sub': 'आपकी कॉल पर वापस जा रहे हैं…',
  'droom.gcap.back': 'कॉल पर वापस जाएँ',
  'droom.gcap.send': 'यह फ़ोटो भेजें',
  'droom.gcap.sending': 'भेजी जा रही है…',
  'droom.gcap.sendingLong': 'आपके सत्यापन अधिकारी को भेजी जा रही है…',
  'droom.gcap.looksSharp': 'साफ़ दिख रही है — अपने सत्यापन अधिकारी को भेजें।',
  'droom.gcap.checkingQuality': 'फ़ोटो की गुणवत्ता जाँची जा रही है…',
  'droom.gcap.camOff': 'आपका कैमरा बंद है — यह फ़ोटो लेने के लिए इसे वापस चालू करें।',
  'droom.gcap.notReady': 'कैमरा अभी तैयार नहीं है — एक पल रुककर फिर कोशिश करें।',
  'droom.gcap.uploadFail': 'यह अपलोड नहीं हो पाई — कृपया फिर कोशिश करें।',
  'droom.gcap.retryLine': 'चलिए एक बार और लेते हैं।',
  'droom.gcap.decline':
    'सहज नहीं हैं? बस अपने सत्यापन अधिकारी को बता दें — वे यह अनुरोध वापस ले सकते हैं, और वह भी दर्ज होता है।',
  'droom.gcap.remote.mayCapture':
    'आप इसे स्थिर पकड़े रहें — आपके सत्यापन अधिकारी आपके लिए फ़ोटो ले सकते हैं, और वह आपको यहीं दिखेगा।',
  'droom.gcap.remote.firing': 'आपके सत्यापन अधिकारी फ़ोटो ले रहे हैं — स्थिर रहें',
  'droom.gcap.preview.dead': 'आपके कैमरे का प्रीव्यू नहीं दिख रहा — कुछ भी कैप्चर नहीं हो रहा है',
  'droom.gcap.preview.retry': 'कैमरा फिर से आज़माएँ',
  'droom.gcap.backToCall': 'कॉल पर वापस जाएँ',
  'droom.gcap.upload.title': 'दस्तावेज़ फ़ाइल के रूप में भेजें',
  'droom.gcap.upload.cta': 'इसके बजाय दस्तावेज़ अपलोड करें',
  'droom.gcap.upload.sending': 'आपकी फ़ाइल भेजी जा रही है…',
  'droom.gcap.upload.grade':
    'कॉल पर ली गई फ़ोटो अधिक मज़बूत प्रमाण है। इसका उपयोग तभी करें जब कैमरा काम न करे — आपके सत्यापन अधिकारी इसे आपके साथ जाँचेंगे।',
  'droom.gcap.upload.failed': 'वह फ़ाइल नहीं भेजी जा सकी — कॉल पर अपने सत्यापन अधिकारी को बताएँ।',
  'droom.gcap.upload.notAllowed':
    'यह दस्तावेज़ फ़ाइल के रूप में नहीं भेजा जा सकता — आपका सत्यापनकर्ता कॉल पर आपकी मदद करेगा।',
  'droom.gcap.upload.already':
    'यह दस्तावेज़ पहले से आपके रिकॉर्ड में है — आपका सत्यापनकर्ता इसे देख सकता है।',
  'droom.gcap.upload.tooBig':
    'यह फ़ाइल बहुत बड़ी है। छोटी फ़ोटो भेजें, या सत्यापनकर्ता से मदद लें।',
  'droom.gcap.upload.wrongType':
    'यह फ़ाइल प्रकार पढ़ा नहीं जा सकता। दस्तावेज़ की JPG या PNG फ़ोटो भेजें।',
  'droom.georecheck.title': 'अपनी वर्तमान लोकेशन साझा करें',
  'droom.georecheck.asked':
    'आपके सत्यापन अधिकारी ने इस कॉल पर आपकी लोकेशन और नेटवर्क की दोबारा जाँच माँगी है।',
  'droom.georecheck.privacy':
    'आपकी लोकेशन और नेटवर्क पता सत्यापन साक्ष्य के रूप में एक बार दर्ज होते हैं — लगातार ट्रैकिंग कभी नहीं होती।',
  'droom.georecheck.share': 'मेरी लोकेशन साझा करें',
  'droom.georecheck.requesting': 'लोकेशन प्राप्त की जा रही है…',
  'droom.georecheck.posting': 'सुरक्षित रूप से दर्ज हो रहा है…',
  'droom.georecheck.done.title': 'लोकेशन आपके सत्यापन अधिकारी के साथ साझा हो गई',
  'droom.georecheck.done.sub': 'आपकी कॉल पर वापस जा रहे हैं…',
  'droom.georecheck.done.missing.title': 'नेटवर्क विवरण ताज़ा हो गए',
  'droom.georecheck.done.missing.sub':
    'लोकेशन उपलब्ध नहीं हो पाई, और कोई बात नहीं — आपके सत्यापन अधिकारी आपके वर्तमान नेटवर्क विवरण देख सकते हैं और कॉल पर आपके साथ आगे बढ़ेंगे।',
  'droom.georecheck.failed': 'अभी यह दर्ज नहीं हो पाया — कृपया फिर कोशिश करें।',
  'droom.docside.front': 'आपके {doc} का अगला भाग',
  'droom.docside.back.now': 'अब आपके {doc} का पिछला भाग',
  'droom.docside.back.optionalNote':
    'पिछले भाग पर पता होता है — हो सके तो जोड़ दें। आपके सत्यापन अधिकारी कॉल पर भी इसे आपके साथ ले सकते हैं।',
  'droom.gcap.doc.titleBack': 'अब अपने {doc} का पिछला भाग दिखाएँ',
  'droom.gcap.side.backTitle': 'अब दस्तावेज़ का पिछला भाग',
  'droom.gcap.side.back': 'दस्तावेज़ पलटें और पिछला भाग बॉक्स के अंदर स्थिर रखें।',
  'droom.gcap.side.front': 'दस्तावेज़ का अगला भाग बॉक्स के अंदर स्थिर रखें।',
  'droom.gcap.doc.titleTyped': 'अपना {doc} कैमरे के सामने दिखाएँ',
  'droom.gcap.flip': 'कैमरा बदलें',
  'droom.gcap.rotate':
    'यह दस्तावेज़ आड़ी (landscape) फ़ोटो में ज़्यादा साफ़ आता है — अपना फ़ोन घुमाकर आड़ा करें।',

  // ── CONFIRM YOUR DETAILS (SPEC-14 §5 golden-source intake) ─────────────────
  'droom.confirm.subtitle': 'आपके विवरण',
  'droom.confirm.aria': 'अपने विवरण की पुष्टि करें',
  'droom.confirm.title': 'अपने विवरण की पुष्टि करें',
  'droom.confirm.notProvided': 'नहीं दिया गया',
  'droom.confirm.sub':
    'ये विवरण आपका सत्यापन शुरू करते समय दिए गए थे — आपकी पहचान इन्हीं से मिलाकर जाँची जाती है। कृपया हर विवरण देख लें।',
  'droom.confirm.loading': 'आपके विवरण लाए जा रहे हैं…',
  'droom.confirm.readonlyNote':
    'अगर यहाँ कुछ गलत है, तो कॉल पर अपने सत्यापन अधिकारी को बताएँ — ये विवरण यहाँ बदले नहीं जा सकते।',
  'droom.confirm.cta': 'ये विवरण सही हैं',
  'droom.confirm.submitting': 'आपकी पुष्टि दर्ज हो रही है…',
  'droom.confirm.err': 'अभी आपकी पुष्टि दर्ज नहीं हो पाई। कृपया फिर कोशिश करें।',

  // ── COMPLETE ───────────────────────────────────────────────────────────────
  // See the English deck: "आप सत्यापित हो गए" (you are verified) claimed a regulatory
  // outcome that does not exist yet — the session is submitted, the review is pending.
  'droom.complete.subtitle': 'जमा किया गया',
  'droom.complete.title': 'हो गया — धन्यवाद',
  'droom.complete.sub':
    'धन्यवाद, {name}। आपने अपना video-KYC पूरा कर लिया है। अब हमारी टीम इसकी समीक्षा करेगी।',
  'droom.complete.nameFallback': 'जी',
  // CD33 A-05 — "रिपोर्ट" (report) promised a result; this document is a submission receipt.
  'droom.complete.download': 'अपनी जमा-रसीद डाउनलोड करें (PDF)',
  'droom.complete.downloadNote':
    'आपने क्या और कब जमा किया — यह कोई निर्णय नहीं है। परिणाम {tenantName} से आएगा।',
  'droom.complete.k.reference': 'संदर्भ',
  'droom.complete.k.completed': 'पूर्ण हुआ',
  'droom.complete.k.status': 'स्थिति',
  'droom.complete.v.submitted': 'समीक्षा के लिए प्रस्तुत',
  'droom.complete.next.review':
    'हमारी टीम समीक्षा कर **आपका खाता सक्रिय करती है** — आमतौर पर कुछ घंटों में।',
  'droom.complete.next.confirm': 'आपको **ईमेल और SMS पर पुष्टि** मिलेगी।',
  'droom.complete.redirect.cta': '{label} पर जाएँ',
  'droom.complete.redirect.countdown': '{s} सेकंड में आपको {label} पर वापस ले जा रहे हैं…',
  'droom.complete.redirect.now': 'आपको {label} पर वापस ले जा रहे हैं…',
  // CD33 A-05 — the button used to name a destination and go nowhere. This branch has nowhere.
  'droom.complete.return': 'यह पेज बंद करें',
  'droom.complete.recorded':
    'अनुपालन के लिए यह सत्र सुरक्षित रूप से रिकॉर्ड किया गया · **Secured by KycHub**',

  // ── media hooks ────────────────────────────────────────────────────────────
  'droom.consenthook.loadfail': 'हम सहमति सूचना लोड नहीं कर पाए। कृपया फिर कोशिश करें।',
  'droom.consenthook.updated': 'सहमति सूचना बदल गई है। कृपया दोबारा पढ़कर पुष्टि करें।',
  'droom.consenthook.recordfail': 'आपकी सहमति अभी दर्ज नहीं हो पाई। कृपया फिर कोशिश करें।',
  'droom.quality.small.doc':
    'यह फ़ोटो दस्तावेज़ पढ़ने के लिए बहुत छोटी है। कार्ड को फ़्रेम में भरने के लिए पास आएँ और फिर से कोशिश करें।',
  'droom.quality.small.selfie':
    'यह फ़ोटो बहुत छोटी है। फ़ोन को थोड़ा पास लाएँ और फिर से कोशिश करें।',
  'droom.quality.dark': 'बहुत अँधेरा है — उजली जगह जाकर फिर कोशिश करें।',
  'droom.quality.bright': 'बहुत तेज़ रोशनी है — सीधी रोशनी या चमक से हटकर फिर कोशिश करें।',
  'droom.quality.blurry': 'धुँधली है — फ़ोन स्थिर रखकर फिर कोशिश करें।',
  'droom.quality.unreadable': 'यह फ़ोटो प्रोसेस नहीं हो पाई — कृपया फिर कोशिश करें।',
};
